import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Badge,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import { preProductionApi } from "../../../../services/preProductionServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCube, faCheck } from "@fortawesome/free-solid-svg-icons";
import Loader from "../../../Loader";

const DONE_STATUS_ID = 3;

const PreProductionSummary = () => {
  const { id: jobId } = useParams();
  const [preProductionItems, setPreProductionItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPreProductionItems = async () => {
      if (jobId) {
        setLoading(true);
        try {
          const response = await preProductionApi.getAllPreProductionItems({
            jobId,
          });
          if (response.data && response.data.length > 0) {
            setPreProductionItems(
              response.data[response.data.length - 1].preProductionItems || []
            );
          } else {
            setPreProductionItems([]);
          }
        } catch (error) {
          console.error("Error fetching pre-production info", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchPreProductionItems();
  }, [jobId]);

  const { doneItems, totalItems } = useMemo(() => {
    return {
      doneItems: preProductionItems.filter(
        (item) => item.preProductionStatus.id === DONE_STATUS_ID
      ).length,
      totalItems: preProductionItems.length,
    };
  }, [preProductionItems]);

  const getStatusBadge = (preProductionStatus) => {
    const isDone = preProductionStatus.id === DONE_STATUS_ID;
    const badgeClass = `p-2 preprod-badge ${
      isDone ? "preprod-badge-done" : "preprod-badge-info"
    }`;
    const dotClass = `preprod-status-dot ${
      isDone ? "preprod-status-dot-done" : "preprod-status-dot-info"
    }`;

    const content = isDone ? (
      <>
        <FontAwesomeIcon icon={faCheck} className="mr-1" />
        {` ${preProductionStatus.name}`}
      </>
    ) : (
      <>
        <span className={dotClass} />
        {` ${preProductionStatus.name}`}
      </>
    );

    return (
      <Badge pill color="light" className={badgeClass}>
        {content}
      </Badge>
    );
  };

  if (!jobId) {
    return null;
  }

  return (
    <Card className="shadow-sm border-0 m-0 h-100">
      <CardHeader className="bg-white border-0 pt-3 pb-0">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div className="rounded-circle d-flex align-items-center justify-content-center p-2 bg-soft-blue">
              <FontAwesomeIcon icon={faCube} size="lg" />
            </div>
            <h4 className="mb-0 ml-3 font-weight-bold">Pre-Production</h4>
          </div>
          <h4 className="mb-0 font-weight-bold">
            {doneItems}/{totalItems}
          </h4>
        </div>
      </CardHeader>
      {loading ? (
        <CardBody className="d-flex">
          <Loader size={"sm"} />
        </CardBody>
      ) : (
        <CardBody className="p-3 pt-0">
          <ListGroup flush className="h-100">
            {preProductionItems.length > 0 ? (
              preProductionItems.map((item) => (
                <ListGroupItem
                  key={item.id}
                  className="d-flex justify-content-between align-items-center border-0 px-0 pb-0 pt-2"
                >
                  <span>{item.preProductionItemType.name}</span>
                  {getStatusBadge(item.preProductionStatus)}
                </ListGroupItem>
              ))
            ) : (
              <ListGroupItem className="d-flex justify-content-center align-items-center border-0 px-0 pb-0 pt-2 h-100">
                <span>No pre-production items for this Job yet</span>
              </ListGroupItem>
            )}
          </ListGroup>
        </CardBody>
      )}
    </Card>
  );
};

export default PreProductionSummary;
